import * as focusTrap from "focus-trap";

// A dialog has to take focus when it opens, keep it while it is open, and hand
// it back when it closes. None of that is expressible in Elm: it is imperative,
// it depends on what was focused before, and it has to survive every re-render.
//
// Ui.Modal renders this element only while the modal is visible, so connecting
// is opening and disconnecting is closing.

// Shared so focus-trap keeps only the topmost trap active when two modals are
// open, instead of two traps fighting over focus.
const trapStack = [];

const openModals = [];

function topmost() {
  return openModals[openModals.length - 1];
}

// Everything that is not on the path from the modal up to the body. Trapping
// Tab is not enough on its own: a screen reader's own cursor does not follow
// focus, so without this it reads straight through the page behind the dialog.
function siblingsAbove(modal) {
  const siblings = [];

  for (let node = modal; node && node !== document.body; node = node.parentNode) {
    if (!node.parentNode) break;

    for (const sibling of node.parentNode.children) {
      if (sibling !== node && !sibling.hasAttribute("inert")) siblings.push(sibling);
    }
  }

  return siblings;
}

let inerted = [];

// Only the topmost modal hides the rest of the page, so a second one opening
// does not un-hide what the first one hid when it closes.
function isolateTopmost() {
  // The attribute, not the property: the property does not reflect to the
  // attribute everywhere, and `[inert]` is what CSS and queries can see.
  for (const element of inerted) element.removeAttribute("inert");

  const modal = topmost();
  inerted = modal ? siblingsAbove(modal) : [];

  for (const element of inerted) element.setAttribute("inert", "");
}

// Escape belongs to the dialog, not to whichever page happened to wire a
// subscription for it, and only the topmost one should answer.
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape" || event.defaultPrevented) return;

  const modal = topmost();
  if (!modal || modal.dataset.dismissible === "false") return;

  event.preventDefault();
  modal.dispatchEvent(new CustomEvent("close"));
});

// A click on another <elm-modal> is "outside" this trap, so without allowing it
// each active trap would preventDefault the other modal's controls.
function isInAnyModal(node) {
  return node.closest("elm-modal") != null;
}

class ElmModal extends HTMLElement {
  connectedCallback() {
    this.returnFocusTo = document.activeElement;
    openModals.push(this);
    isolateTopmost();

    // Whether focus was ever ours has to be recorded while we are still in the
    // document: by the time we disconnect, Elm has removed the panel and
    // document.activeElement is already the body.
    this.heldFocus = false;
    this.onFocusIn = () => {
      this.heldFocus = true;
    };
    this.addEventListener("focusin", this.onFocusIn);

    this.trap = focusTrap.createFocusTrap(this, {
      allowOutsideClick: (event) => isInAnyModal(event.target),
      // Escape closes through Elm, which is what removes this element.
      escapeDeactivates: false,
      // disconnectedCallback decides where focus goes, and by then the panel
      // is already gone.
      returnFocusOnDeactivate: false,
      // The dialog itself, so a reader announces its name before its contents.
      // The backdrop dismiss button is first in the DOM and is a poor landing.
      initialFocus: () => this.querySelector('[role="dialog"]') ?? false,
      setReturnFocus: () => false,
      trapStack,
    });

    // Elm paints on an animation frame, so the panel is not here yet.
    this.activation = requestAnimationFrame(() => this.activate());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.activation);
    this.trap?.deactivate();
    this.trap = undefined;
    this.removeEventListener("focusin", this.onFocusIn);

    const index = openModals.indexOf(this);
    if (index !== -1) openModals.splice(index, 1);

    isolateTopmost();

    // Only restore what we took, and only if nothing else is holding focus
    // now — a modal closing underneath another would otherwise yank focus out
    // of the one still open.
    if (this.heldFocus && !topmost() && this.returnFocusTo?.isConnected) {
      this.returnFocusTo.focus();
    }

    this.returnFocusTo = undefined;
    this.heldFocus = false;
  }

  activate() {
    const dialog = this.querySelector('[role="dialog"]');
    if (dialog) dialog.tabIndex = -1;

    this.trap?.activate();
  }
}

if (!customElements.get("elm-modal")) {
  customElements.define("elm-modal", ElmModal);
}
